<template>
  <div
    :class="[
      modClass ? `date-component-${modClass}` : '',
      { 'date-component--with-icon': dateIcon },
      { 'date-component--label-left': labelLeft },
      { 'date-component--error': v$.date.$error || error },
    ]"
  >
    <label :for="id" v-if="label || labelLeft" class="label-field" v-html="label || labelLeft"></label>
    <div class="date-component__inner">
      <datepicker
        :key="datePickerKey"
        ref="datepicker"
        :id="id"
        :startingView="getStartingView"
        :locale="locale"
        :inputFormat="getDateFormat"
        v-model="date"
        :placeholder="getPlaceholder"
        :style="{ background: !disabled ? '#fff' : null }"
        :clearable="clearable && !disabled"
        :disabled="disabled"
        :required="required"
        :class="[{ error: v$.date.$error || error }]"
        :typeable="typeable"
        :lowerLimit="lowerLimit"
        :upperLimit="upperLimit"
        :minimumView="getMinimumView"
        position="center"
        @blur="blurHandler"
      />
      <svg
        v-if="dateIcon"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        class="svg"
        :class="{ svg_disabled: disabled }"
        @click="openCalendar"
      >
        <path
          d="M15.5009 2.5V7.5M19.0714 10.062V6.3C19.0714 5.57098 18.4958 4.98 17.7857 4.98H6.2143C5.50422 4.98 4.92859 5.57098 4.92859 6.3V10.062M19.0714 10.062V18.18C19.0714 18.909 18.4958 19.5 17.7857 19.5H6.2143C5.50422 19.5 4.92859 18.909 4.92859 18.18V10.062M19.0714 10.062H4.92859M8.50085 2.5V7.5"
          stroke-width="2"
        />
      </svg>
    </div>
    <div class="invalid-msg">
      <template v-if="v$.date.$error">
        <template v-if="v$.date.required.$invalid">Поле обязательно для заполнения</template>
        <template v-else-if="v$.date.minValue.$invalid">
          Значение должно быть не ранее
          {{ $momentFormat(lowerLimit, getPreset.limitDisplayFormat) }}
        </template>
        <template v-else-if="v$.date.maxValue.$invalid">
          Значение должно быть не позднее
          {{ $momentFormat(upperLimit, getPreset.limitDisplayFormat) }}
        </template>
        <template v-else-if="v$.date.validDate.$invalid"> Неверный формат даты. </template>
      </template>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core';
  import { maxValue, minValue, requiredIf } from '@vuelidate/validators';
  import Inputmask from 'inputmask';
  import moment from 'moment';

  import Constants from '@/common/constants';
  import { dateRu } from '@/common/constants/datePickerLocale';

  import { DATE_PICKER_MODE_PRESETS, DATE_PICKER_MODES } from './dateComponentMode';
  import { getInputMaskOptions } from './inputMaskOptions';

  export default {
    name: 'DateComponent',
    props: {
      modelValue: String,
      mode: {
        type: String,
        default: 'date',
        validator: (value) => DATE_PICKER_MODES.includes(value),
      },
      clearable: {
        type: Boolean,
        default: true,
      },
      required: {
        type: Boolean,
        default: false,
      },
      isDirtyValidation: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      id: String,
      label: {
        type: String,
        default: '',
      },
      typeable: {
        type: Boolean,
        default: true,
      },
      lowerLimit: {
        type: Date,
        default: Constants.dateLowerLimit,
      },
      upperLimit: {
        type: Date,
        default: Constants.dateUpperLimit,
      },
      labelLeft: {
        type: String,
      },
      modClass: {
        type: String,
        default: '',
        validator: (value) =>
          [
            'redesigned',
            'redesigned popover-top',
            'modal-request',
            'redesign-type-exponential',
            'filter-small',
            'filter',
          ].includes(value),
      },
      placeholder: {
        type: String,
        default: undefined,
      },
      dateIcon: {
        type: Boolean,
      },
      error: {
        type: Boolean,
        default: false,
      },
      // Заказчик не согласовал виды работ по добавлению маски, но уже сделали, поэтому по дефолту false
      hasInputMask: {
        type: Boolean,
        default: false,
      },
    },
    created() {
      if (this.modelValue) {
        this.date = this.parseModelValueToDate(this.modelValue);
      }
    },
    mounted() {
      if (this.hasInputMask) {
        this.$nextTick(() => this.initInputMask());
      }
    },
    beforeUnmount() {
      this.destroyInputMask();
    },
    computed: {
      getPreset() {
        return DATE_PICKER_MODE_PRESETS[this.mode];
      },
      getDateFormat() {
        return this.getPreset.inputFormat;
      },
      getMinimumView() {
        return this.getPreset.minimumView;
      },
      getStartingView() {
        return this.getPreset.startingView;
      },
      getInputMask() {
        return this.getPreset.inputMask;
      },
      getPlaceholder() {
        return this.placeholder ?? this.getPreset.defaultPlaceholder;
      },
    },
    data() {
      return {
        date: null,
        locale: dateRu,
        datePickerKey: 0,
        stableLowerLimit: this.lowerLimit,
        stableUpperLimit: this.upperLimit,
      };
    },
    methods: {
      normalizeMomentToDate(momentValue) {
        switch (this.mode) {
          case 'year':
            return momentValue.startOf('year').toDate();
          case 'yearMonth':
            return momentValue.startOf('month').toDate();
          default:
            return momentValue.startOf('day').toDate();
        }
      },
      parseModelValueToDate(value) {
        if (value == null || value === '') {
          return null;
        }

        const parsedStrict = moment(String(value), this.getPreset.parseFormats, true);
        if (parsedStrict.isValid()) {
          return this.normalizeMomentToDate(parsedStrict);
        }

        const parsedLoose = moment(value);
        return parsedLoose.isValid() ? parsedLoose.startOf('day').toDate() : null;
      },
      formatDateToModelValue(value) {
        if (value == null || value === 'Invalid Date') {
          return null;
        }

        if (this.getPreset.emitFormat === Constants.DATE_FORMATS.YYYY) {
          return moment(value).format(Constants.DATE_FORMATS.YYYY);
        }

        return moment(value).format(Constants.DATE_FORMATS['YYYY-MM-DD']);
      },
      blurHandler(event) {
        const raw = String(event.target.value || '').trim();
        const parsedMoment = moment(raw, this.getPreset.blurMomentFormat, true);

        this.v$.date.$touch();

        if (!raw || !parsedMoment.isValid()) {
          this.resetDateInput();
          return;
        }

        const normalizedDate = this.normalizeMomentToDate(parsedMoment);
        if (normalizedDate < this.lowerLimit || normalizedDate > this.upperLimit) {
          this.resetDateInput();
          return;
        }

        this.date = normalizedDate;
      },
      resetDateInput() {
        this.date = null;

        this.datePickerKey++;
      },
      openCalendar() {
        this.$refs.datepicker.inputRef.focus();
      },
      initInputMask() {
        if (!this.typeable || this.disabled || !this.hasInputMask) return;

        const input = this.$refs.datepicker.inputRef;

        if (!input) return;

        this.destroyInputMask(input);

        const im = new Inputmask(getInputMaskOptions(this.getInputMask));
        im.mask(input);
      },

      destroyInputMask(inputRef = this.$refs.datepicker?.inputRef) {
        const input = inputRef;

        if (input) Inputmask.remove(input);
        this.inputMaskInstance = null;
      },
    },
    watch: {
      modelValue(newValue) {
        if (newValue == null) {
          this.date = null;
          return;
        }

        this.date = this.parseModelValueToDate(newValue);
      },
      date(newValue) {
        const hasNewValue = newValue != null && newValue !== 'Invalid Date';
        this.$emit('update:modelValue', hasNewValue ? this.formatDateToModelValue(newValue) : null);
      },
      lowerLimit(newValue, oldValue) {
        if (newValue?.getTime?.() !== oldValue?.getTime?.()) this.stableLowerLimit = newValue;
      },
      upperLimit(newValue, oldValue) {
        if (newValue?.getTime?.() !== oldValue?.getTime?.()) this.stableUpperLimit = newValue;
      },
      disabled: function (newValue) {
        if (newValue) {
          this.date = null;
        }
      },
      datePickerKey() {
        this.$nextTick(() => this.initInputMask());
      },
    },
    setup: () => ({ v$: useVuelidate() }),
    validations() {
      return {
        date: {
          required: requiredIf(function () {
            return this.required;
          }),
          minValue: minValue(this.stableLowerLimit),
          maxValue: maxValue(this.stableUpperLimit),
          $autoDirty: this.isDirtyValidation,
          validDate: (value) => {
            if (!value) {
              return true;
            }
            return moment(value).isValid();
          },
        },
      };
    },
  };
</script>

<style lang="scss">
  .date-component-redesigned {
    position: relative;
    display: flex;
    flex-direction: column;

    .date-component__inner {
      position: relative;
      width: 100%;
    }

    .invalid-msg {
      min-height: 32px;
    }

    .v3dp__datepicker {
      width: 100%;

      .v3dp__popout {
        padding-bottom: 12px;
        margin-top: 0;
        transform: translateY(0);
        transition: transform 0.3s ease;
      }

      .v3dp__heading__button {
        width: 24px;
      }

      .v3dp__heading__center {
        font: $fira-14-20;
        font-weight: 500;
      }

      .v3dp__heading__icon {
        width: 24px;
      }
      .v3dp__heading {
        padding: 16px 8px 8px 8px;
        height: auto;
        flex: 0;
      }
      .v3dp__body {
        font-family: 'Fira Sans';
        line-height: 24px;
      }
      .v3dp__subheading {
        margin-top: 8px;
      }

      .v3dp__divider {
        border-color: $grey;
        margin: 12px 0;
      }

      .v3dp__elements {
        button {
          span {
            padding: 4px;
            font-family: 'Fira Sans';
            font-size: 12px;
            line-height: 16px;
          }
        }
      }

      .v3dp__input_wrapper {
        input {
          padding: 8px 44px 8px 14px;
          font: $fira-14-20;
          border-radius: 4px;
          border: 1px solid $grey;
          background: $light-grey;
          height: 40px;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          box-sizing: border-box;

          &:focus {
            border-color: $green !important;
          }

          &.error {
            border-color: $red !important;
          }

          &:disabled {
            border: none;
            background: $grey-disabled !important;
          }

          &:disabled::after {
            opacity: 0;
          }
        }
        .v3dp__clearable {
          position: absolute;
          left: auto;
          top: 50%;
          transform: translateY(-50%);
          right: 8px;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 3;
          mask-image: none;
          background-color: transparent;
          transition: 0.3s;

          &:hover {
            background-color: transparent;
          }

          i {
            position: absolute;
            inset: 0;
            opacity: 0;
            width: 100%;
            height: 100%;
          }

          &::after {
            content: '';
            display: inline-block;
            width: 10px;
            height: 18px;
            background-color: #999;
            -webkit-mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 320 512' fill='currentColor' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m207.6 256 107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z'/%3E%3C/svg%3E");
            mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg viewBox='0 0 320 512' fill='currentColor' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m207.6 256 107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z'/%3E%3C/svg%3E");
            -webkit-mask-position: center;
            mask-position: center;
            -webkit-mask-repeat: no-repeat;
            mask-repeat: no-repeat;
            -webkit-mask-size: contain;
            mask-size: contain;
            transition: 0.3s;
            pointer-events: none;
          }

          &:hover::after {
            background-color: $black-st;
          }
        }
      }
    }

    &.date-component--error {
      .v3dp__datepicker {
        .v3dp__popout {
          transform: translateY(40px);
        }
      }
    }

    &.date-component--with-icon {
      .v3dp__datepicker {
        .v3dp__input_wrapper {
          input {
            padding-right: 60px;
          }
          .v3dp__clearable {
            right: 40px;
          }
        }
      }
    }

    .svg {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 8px;
      width: 24px;
      height: 24px;
      z-index: 2;
      cursor: pointer;
      stroke: $base;
    }
    .svg_disabled {
      stroke: $light-grey-5;
    }

    &.small {
      .v3dp__datepicker {
        .v3dp__input_wrapper {
          input {
            height: 40px;
          }
        }
      }
    }

    .filter-block-ves-events & {
      flex: 1 1;
    }

    &.popover-left {
      .v3dp__popout {
        right: auto;
        left: 0;
      }
    }

    &.popover-top {
      .v3dp__popout {
        right: auto;
        left: 0;
        bottom: 100%;
        top: auto !important;
        margin-bottom: 5px;
      }
    }
  }

  .date-component-modal-request,
  .date-component-filter {
    .date-component__inner {
      position: relative;
      width: 100%;
    }

    .svg {
      position: absolute;
      top: 7px;
      right: 8px;
      width: 24px;
      height: 24px;
      z-index: 2;
      cursor: pointer;
      stroke: $dark-grey-1;
    }

    .v3dp__input_wrapper {
      input {
        border-radius: 4px;
        padding: 9px 14px;
        font: $fira-14-20;

        &:focus {
          border-color: $green !important;
        }

        &.disabled,
        &:disabled {
          background: $grey-disabled !important;
          border-color: $grey-disabled !important;
        }

        &:focus {
          border-color: $grey;
        }

        &::placeholder {
          letter-spacing: 0;
        }
      }

      .v3dp__clearable {
        position: absolute;
        right: 10px;
        top: 11px;
        left: auto;
      }
    }

    .bulk-editing-popup & {
      flex: 1;
    }
  }

  .date-component-filter {
    &:not(:first-of-type) {
      margin-top: 12px;
    }
  }

  .v3dp__popout {
    right: 0;
    z-index: 11110 !important;

    .form-create-request & {
      bottom: 100%;
      right: 0;
      left: 0;
    }
  }

  .v3dp__input_wrapper {
    position: relative;
    background: #ffffff;
    border: 1px solid rgba(195, 195, 200, 0.5);
    input {
      background: #fbfbfb !important;
      width: 100%;
      display: inline-block;

      &.datepicker {
        &--rectangular {
          border-radius: 4px;
          padding: 9px 14px;
          font: $fira-14-20;

          &::placeholder {
            font-size: 16px;
            line-height: 24px;
          }

          &:focus {
            border-color: $grey;
          }
        }
      }
    }
    .v3dp__clearable {
      position: absolute;
      right: 10px;
      top: 6px;
      left: auto;
    }
  }

  .date-component {
    &-redesign-type-exponential {
      display: flex;
      align-items: center;
      position: relative;

      .label-field {
        margin: 0 12px 0 0;
      }

      .svg {
        stroke: black;
        position: absolute;
        right: 9px;
        bottom: 9px;
        cursor: pointer;
      }

      .date-component__inner {
        flex: 1 1 auto;
      }

      .v3dp {
        &__input_wrapper {
          input {
            border-radius: 4px;
            padding: 9px 14px;
          }
        }

        &__input {
          &_wrapper {
            .v3dp__clearable {
              right: 38px;
              top: 50%;
              transform: translateY(-50%);
              width: 18px;
              height: 18px;

              &::before {
                content: '';
                position: absolute;
                background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg viewBox=%270 0 320 512%27 fill='%23999999' xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cpath d=%27M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z%27/%3E%3C/svg%3E");
                background-repeat: no-repeat;
                background-size: contain;
                width: 13px;
                height: 17px;
                inset: 0;
                margin: auto;
                z-index: 1;
              }

              i {
                position: absolute;
                inset: 0;
                font-size: 0;
                z-index: 2;
              }

              &:hover {
                &::before {
                  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg viewBox=%270 0 320 512%27 fill='%23000000' xmlns=%27http://www.w3.org/2000/svg%27%3E%3Cpath d=%27M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z%27/%3E%3C/svg%3E");
                }
              }
            }
          }
        }
      }
    }
  }

  .height-40 .v3dp__datepicker .v3dp__input_wrapper input {
    height: 40px;
  }

  .date-component-filter-small {
    &:not(:first-child) {
      margin-top: 5px;
    }

    .horizontally-date & {
      margin: 0 !important;

      .v3dp__popout {
        right: 0;
        transform: translateX(50%);
      }
    }

    .date-component {
      &__inner {
        position: relative;
        width: 100%;

        .svg {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          right: 8px;
          width: 12px;
          height: 12px;
          z-index: 2;
          cursor: pointer;
          stroke: $dark-grey-1;
        }
      }
    }

    .v3dp__input_wrapper {
      input {
        border-radius: 4px;
        padding: 8px;
        font: $fira-12;

        &.disabled,
        &:disabled {
          background: $grey-disabled !important;
          border-color: $grey-disabled !important;
        }

        &::placeholder {
          font: $fira-12;
        }
      }

      .v3dp__clearable {
        right: 6px;
        top: 50%;
        transform: translateY(calc(-50% - 1px));
        font-size: 12px;
        line-height: 1;
        color: $dark-grey-1;
      }
    }
  }

  .date-component--label-left {
    display: flex;
    flex-direction: row;
    align-items: center;

    .label-field {
      margin: 0 12px 0 0;
      white-space: nowrap;
    }

    .date-component__inner {
      flex: 1;
    }
  }

  .bulk-editing-popup {
    .v3dp__popout {
      right: auto;
      left: 0;
      bottom: 100%;
    }
  }

  .date-component__inner {
    input::placeholder {
      overflow: visible;
    }
  }
</style>
